/**
 * Troca da própria senha, pelo rodapé do menu.
 *
 * Fecha o ciclo de convite: o administrador cria o usuário com uma senha
 * provisória e a passa por WhatsApp; quem entra troca aqui na primeira vez.
 * Sem esta tela, a provisória virava definitiva — e ficava conhecida por
 * duas pessoas.
 *
 * Não há "esqueci a senha": o sistema ainda não envia e-mail. Quem esquecer
 * pede ao administrador, que define outra pela tela de Usuários.
 */
import { SENHA_MAX, SENHA_MIN, mensagemSenhaInvalida } from '@simpleadm/shared-types';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { CampoSenha } from '@/components/CampoSenha';
import { RequisitosSenha } from '@/components/RequisitosSenha';
import { ApiError, apiFetch } from '@/lib/api';


export function AlterarSenhaDialog({ onClose }: { onClose: () => void }) {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState(false);

  const trocar = useMutation({
    mutationFn: () =>
      apiFetch<void>('/users/me/password', {
        method: 'PATCH',
        body: JSON.stringify({ currentPassword: atual, newPassword: nova }),
      }),
    onSuccess: () => setFeito(true),
    onError: (e) =>
      setErro(
        e instanceof ApiError && e.status === 401
          ? 'A senha atual não confere.'
          : e instanceof ApiError
            ? e.message
            : 'Não foi possível trocar a senha.',
      ),
  });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    // A mesma função que a API usa — a tela não tem regra própria.
    const problema = mensagemSenhaInvalida(nova);
    if (problema) return setErro(problema);
    if (nova !== confirmacao) return setErro('A confirmação não bate com a nova senha.');
    if (nova === atual) return setErro('A nova senha é igual à atual.');
    trocar.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={enviar}
        className="w-full max-w-sm space-y-4 rounded-lg bg-card p-6 text-card-foreground shadow-xl"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <KeyRound className="h-5 w-5" /> Alterar senha
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {feito ? (
          <>
            <p className="text-sm">
              Senha alterada. Vale a partir do próximo login — a sessão atual continua aberta.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <CampoSenha
              label="Senha atual"
              required
              autoComplete="current-password"
              value={atual}
              onChange={(e) => setAtual(e.target.value)}
            />
            <CampoSenha
              label="Nova senha"
              required
              minLength={SENHA_MIN}
              maxLength={SENHA_MAX}
              autoComplete="new-password"
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              abaixo={<RequisitosSenha senha={nova} />}
            />
            <CampoSenha
              label="Repita a nova senha"
              required
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />

            {erro && <p className="text-sm text-destructive">{erro}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={trocar.isPending}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {trocar.isPending ? 'Salvando…' : 'Salvar nova senha'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
